import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/src/components/ui/dialog";
import { Button } from "@/src/components/ui/button";
import { Input } from "@chakra-ui/react";
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Get, Put } from "@/src/api";
import { Pencil } from "lucide-react";
import { useToast } from "@chakra-ui/react";

interface AccountManager {
  firstName: string;
  lastName: string;
  mail: string;
  id: number;
}

interface AccountManagerDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  accountManagerId: number | null;
}

const AccountManagerDetails: React.FC<AccountManagerDetailsProps> = ({
  isOpen,
  onClose,
  accountManagerId,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Partial<AccountManager>>({});

  const { data: accountManager, isLoading } = useQuery<AccountManager | null>({
    queryKey: ["accountManager", accountManagerId],
    queryFn: async () => {
      if (!accountManagerId) return null;
      const result = await Get(
        `/support/admin/account-managers/${accountManagerId}`,
      );
      return result as AccountManager;
    },
    enabled: isOpen && accountManagerId !== null,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<AccountManager>) => {
      if (!accountManagerId) throw new Error("No account manager selected");
      return Put(`/support/admin/account-managers/${accountManagerId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accountManager", accountManagerId],
      });
      queryClient.invalidateQueries({ queryKey: ["accountManagers"] });
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Account manager updated successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update account manager",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleEdit = () => {
    if (accountManager) {
      setEditedData({
        firstName: accountManager.firstName,
        lastName: accountManager.lastName,
        mail: accountManager.mail,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editedData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({});
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle>Account Manager Details</DialogTitle>
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEdit}
                className="p-4"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {isLoading ? (
            <div>Loading...</div>
          ) : accountManager ? (
            <>
              <div className="space-y-2">
                <h4 className="font-medium leading-none">First Name</h4>
                {isEditing ? (
                  <Input
                    value={editedData.firstName}
                    onChange={(e) =>
                      setEditedData({
                        ...editedData,
                        firstName: e.target.value,
                      })
                    }
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {accountManager.firstName}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Last Name</h4>
                {isEditing ? (
                  <Input
                    value={editedData.lastName}
                    onChange={(e) =>
                      setEditedData({ ...editedData, lastName: e.target.value })
                    }
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {accountManager.lastName}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Email</h4>
                {isEditing ? (
                  <Input
                    value={editedData.mail}
                    onChange={(e) =>
                      setEditedData({ ...editedData, mail: e.target.value })
                    }
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {accountManager.mail}
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>
        {isEditing && (
          <DialogFooter>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountManagerDetails;
